import React, { useRef, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import HomeIcon from '@mui/icons-material/Home';
import Popper from '@mui/material/Popper';
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { faMapMarkerAlt, faRoad } from '@fortawesome/free-solid-svg-icons';
import { InputRow } from '../InputRow';
import { AIRPORTS } from '../airports';
import { AlTextField } from '../../../../common/components/AlTextField';
import { GERMANY } from '../../../../constants';
import { listItemLabel } from './listItemLabel';
import { useAddressFields } from './useAddressFields';

const AddressFields = ({
    transfer,
    updateTransfer,
    name,
    touchTransfer,
    transferError,
    disabled,
    user,
    t,
    search,
    getPlace,
    searchPlaceId,
    logError,
    language,
    inlineStreetNumber = false,
}) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const { address, listGroups, errorMessage, showLoadingSpinner, setErrorMessage, listOnSelect } = useAddressFields({
        transfer,
        name,
        updateTransfer,
        t,
        search,
        getPlace,
        searchPlaceId,
        logError,
        language,
    });

    const needStreetNumber =
        !!address.id &&
        AIRPORTS.every((airportCode) => airportCode !== address.id) &&
        address.street !== '--' &&
        !address.streetNumber;
    const autoFocus = name === 'addressFrom';
    const placeValue = address.placeValue;
    // transferFormValidationErrors requires gPlaceId (address picked from the Google list)
    // but no field renders it — surface it on the visible place input so the always-enabled
    // continue button has something to highlight (spec §5.1). Falsy-safe for the widget's
    // no-op transferError stub.
    const gPlaceIdErrorMessage = transferError(`${name}.gPlaceId`) ? 'booking.errorNoValueSelected' : null;
    const [listAnchorEl, setListAnchorEl] = useState(null);
    const listInputHostEl = useRef(null);
    const listInputEl = useRef(null);
    const listPopperEl = useRef(null);
    const open = Boolean(listAnchorEl);
    const listId = open ? `popper-${name}` : undefined;
    const selected = Boolean(address.id);

    const listInputOnFocus = () => {
        setListAnchorEl(listInputHostEl.current);
    };

    const listInputOnClick = () => {
        setListAnchorEl(listInputHostEl.current);
    };

    const listInputOnBlur = () => {
        if (!disabled && !selected && placeValue) {
            setErrorMessage('booking.errorNoValueSelected');
        }

        if (!address.id) {
            updateTransfer({
                name,
                value: {
                    ...address,
                    zip: '',
                    place: address.placeValue,
                },
            });
        }

        setListAnchorEl(null);
    };

    const handleSelect = (item) => {
        listOnSelect(item);
        setListAnchorEl(null);
    };

    const listInputChange = (e) => {
        updateTransfer({
            name,
            value: {
                id: '',
                zip: address.zip || '',
                place: address.place || '',
                street: AIRPORTS.includes(address.id) ? '' : address.street || '',
                name: '',
                phone: '',
                countryId: GERMANY,
                placeValue: e.target.value,
            },
        });
        setListAnchorEl(listInputEl.current);
    };

    const listMoveOffsetMap = { 38: -1, 40: 1, 33: -10, 34: 10 };

    const listInputOnKeyDown = (e) => {
        switch (e.keyCode) {
            case 38: // arrow up
            case 40: // arrow down
            case 33: // page up
            case 34: // page down
                if (!listAnchorEl) {
                    setListAnchorEl(listInputEl.current);
                } else {
                    listMoveCurrentIndex(listPopperEl.current, listMoveOffsetMap[e.keyCode]);
                }
                break;
            case 27: // esc
                setListAnchorEl(null);
                break;
            case 13: // enter
                if (listAnchorEl) {
                    listSelectCurrentIndex(listPopperEl.current, e);
                }
                break;
            default:
                break;
        }
    };

    const listOnMouseDown = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const listClear = () => {
        updateTransfer({
            name,
            value: {
                countryId: GERMANY,
            },
        });
    };

    const selectHome = () => {
        const item = {
            place: user.address.place,
            street: user.address.street,
            zip: user.address.zip,
        };
        updateTransfer({
            name,
            value: {
                ...item,
                placeValue: listItemLabel(item),
                countryId: GERMANY,
            },
        });
    };

    const homeButton = user && user.address && (
        <InputAdornment position="end">
            <IconButton onClick={selectHome}>
                <HomeIcon />
            </IconButton>
        </InputAdornment>
    );

    const clearButton = selected && (
        <InputAdornment position="end">
            <IconButton onClick={listClear}>
                <ClearIcon />
            </IconButton>
        </InputAdornment>
    );

    const adornment = disabled ? null : clearButton || homeButton;

    const updateStreetNumber = (e) => {
        const streetNumber = e.target.value;

        updateTransfer({
            name,
            value: {
                ...address,
                // to update street value with every typing of number
                street: `${address.street.split(',')[0]}, ${streetNumber}`,
                number: streetNumber,
            },
        });
    };

    const addressInput = (
        <Box sx={{ position: 'relative' }} ref={listInputHostEl}>
            <AlTextField
                alRef={listInputEl}
                fullWidth
                name={`${name}.placeValue`}
                placeholder={t(`placeholder.${name}`)}
                error={transferError(`${name}.placeValue`) || transferError(`${name}.gPlaceId`)}
                alTooltip={t(`tooltip.${name}`)}
                required={true}
                disabled={disabled || selected}
                onChange={listInputChange}
                onFocus={listInputOnFocus}
                onClick={() => !disabled && !selected && listInputOnClick()}
                onBlur={listInputOnBlur}
                onKeyDown={listInputOnKeyDown}
                value={placeValue || ''}
                autoFocus={autoFocus}
                slotProps={{
                    input: {
                        endAdornment: showLoadingSpinner ? <CircularProgress color="inherit" size={20} /> : adornment,
                    },
                }}
            />
            <Popper
                ref={listPopperEl}
                id={listId}
                open={open}
                anchorEl={listAnchorEl}
                container={listInputHostEl.current}
                placement="bottom-start"
                onMouseDown={listOnMouseDown}
                sx={{
                    backgroundColor: 'background.paper',
                    zIndex: 2,
                    width: '100%',
                    borderRadius: '4px',
                    marginTop: '3px',
                    overflow: 'auto',
                    boxSizing: 'border-box',
                    boxShadow: '1px 1px 4px rgba(0, 0, 0, 0.4)',
                    maxHeight: '50vh',
                }}
            >
                {listGroups.map((group, key) => {
                    if (!group.items.length) {
                        return null;
                    }
                    return (
                        <React.Fragment key={key}>
                            <Typography variant="subtitle2" sx={{ pt: 1, pl: 1, pb: 0.5, fontWeight: 600 }}>
                                {t(group.label)}
                            </Typography>
                            <Box
                                component="ul"
                                sx={{
                                    p: 0,
                                    m: 0,
                                    listStyle: 'none',

                                    '& > li': {
                                        p: (t) => t.spacing(0.75, 2),
                                    },

                                    '& > li:hover': {
                                        bgcolor: (t) => t.palette.grey[100],
                                        cursor: 'pointer',
                                    },
                                }}
                            >
                                {group.items.map((listItem, key) => (
                                    <Typography
                                        variant="subtitle2"
                                        component="li"
                                        key={key}
                                        onClick={() => handleSelect(listItem)}
                                    >
                                        {listItem.label}
                                    </Typography>
                                ))}
                            </Box>
                        </React.Fragment>
                    );
                })}
            </Popper>
        </Box>
    );

    const streetNumberInput = (
        <AlTextField
            fullWidth
            name={`${name}.number`}
            type="text"
            placeholder={t('placeholder.streetNumber')}
            onChange={updateStreetNumber}
            onBlur={touchTransfer}
            error={transferError(`${name}.number`)}
            value={transfer[name].number || ''}
            alTooltip={t(`tooltip.${name}Street`)}
            required={true}
        />
    );

    if (inlineStreetNumber && isDesktop && needStreetNumber) {
        return (
            <InputRow
                icon={faMapMarkerAlt}
                errorMessage={errorMessage || gPlaceIdErrorMessage}
                sx={{ marginBottom: 0 }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1 }}>{addressInput}</Box>
                    <Box sx={{ width: '80px', flexShrink: 0 }}>{streetNumberInput}</Box>
                </Box>
            </InputRow>
        );
    }

    return (
        <>
            <InputRow icon={faMapMarkerAlt} errorMessage={errorMessage || gPlaceIdErrorMessage}>
                {addressInput}
            </InputRow>
            {needStreetNumber && <InputRow icon={faRoad}>{streetNumberInput}</InputRow>}
        </>
    );
};

function listMoveCurrentIndex(listEl, offset) {
    if (!listEl) {
        return;
    }

    const items = listEl.querySelectorAll('li');
    const itemsCount = items.length;

    if (!itemsCount) {
        return;
    }

    const activeEl = listEl.ownerDocument.activeElement;
    let currentItemIndex = Array.prototype.indexOf.call(items, activeEl);

    currentItemIndex += offset;

    if (currentItemIndex < 0) {
        currentItemIndex = 0;
    }

    if (currentItemIndex > itemsCount - 1) {
        currentItemIndex = itemsCount - 1;
    }

    items[currentItemIndex].focus();
}

function listSelectCurrentIndex(listEl, e) {
    if (!listEl) {
        return;
    }

    const activeEl = listEl.ownerDocument.activeElement;

    if (activeEl.tagName === 'LI') {
        activeEl.click();
        e.preventDefault();
        e.stopPropagation();
    }
}

export { AddressFields };
